import React, { Component } from "react";
import propTypes from "prop-types";
import { observer, inject } from "mobx-react";
import withRouter from "../../components/withRouter";
import t from "../../service/translate.service";
import LoadingPageOverlay from "../../components/LoadingPageOverlay";
import { Alert, DatePicker, Table, Button } from "antd";
import {
  extractError,
  extractErrorFromSap,
} from "../../service/response.service";
import { appFeatureEnabled } from "../../service/appFeatures.service";
import NoContent from "../../components/NoContent";
import { ContractsImage } from "../../components/icons";
import dayjs from "dayjs";
import "dayjs/locale/de";
import "dayjs/locale/en";
import deDE from "antd/es/date-picker/locale/de_DE";
import enUS from "antd/es/date-picker/locale/en_US";

class CondoIncome extends Component {
  static propTypes = {
    contractStore: propTypes.object.isRequired,
  };

  constructor(props) {
    super(props);
    this.state = {
      selectedMonth: null,
      initialLoading: true,
      errorMsg: "",
    };
  }

  async componentDidMount() {
    window.scrollTo(0, 0);

    // Set dayjs locale based on app language
    const currentLocale = t.locale || "de";
    dayjs.locale(currentLocale);

    const contractNumber = this.props.useParams.contractNumber;

    try {
      // Get contract and role information
      await this.props.contractStore.initContracts();
      const selectedContract = this.props.contractStore.activeContracts?.find(
        (c) => c.contractNumber === contractNumber
      );

      if (!selectedContract) {
        this.setState({
          initialLoading: false,
          errorMsg: t.ERROR || "Contract not found",
        });
        return;
      }

      const role = this.props.contractStore.role;

      // Check if user has permission to view income
      if (
        !appFeatureEnabled("condoReceiptInspectionFeaturesEnabled", [selectedContract]) ||
        !appFeatureEnabled("pageCondoIncomeVisibleTo", [
          { ...selectedContract, role },
        ])
      ) {
        this.setState({
          initialLoading: false,
          errorMsg: "Access denied",
        });
        return;
      }

      // Don't fetch data automatically - wait for user to select a month
      this.setState({ initialLoading: false });
    } catch (err) {
      this.setState({
        initialLoading: false,
        errorMsg:
          extractErrorFromSap(err) || t[extractError(err)] || t["ERROR"],
      });
    }
  }

  fetchIncome = async () => {
    const { selectedMonth } = this.state;
    const contractNumber = this.props.useParams.contractNumber;

    try {
      await this.props.contractStore.fetchCondoIncome(
        contractNumber,
        selectedMonth
      );
    } catch (err) {
      this.setState({
        errorMsg:
          extractErrorFromSap(err) || t[extractError(err)] || t["ERROR"],
      });
    }
  };

  handleMonthChange = (date) => {
    if (date) {
      const monthString = date.format("YYYY-MM");
      this.setState({ selectedMonth: monthString }, () => {
        this.fetchIncome();
      });
    } else {
      // Handle clearing the date picker
      this.setState({ selectedMonth: null });
    }
  };

  formatCurrency = (amount, currency = "EUR") => {
    const numAmount = parseFloat(amount || 0);
    return `${numAmount.toLocaleString("de-DE", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })} ${currency}`;
  };

  // Only these condtypes should be shown in the table (explicit allowlist)
  ALLOWED_CONDTYPES = ["W100", "W120", "W160", "W180"];

  // Translate income type based on condtype
  getIncomeTypeTranslation = (condtype) => {
    if (!condtype) return "N/A";

    const currentLocale = t.locale || "de";

    // Translation mappings indexed by condtype
    const translations = {
      W100: {
        de: "Hausgeld umlagefähig",
        en: "Allocable Costs",
      },
      W120: {
        de: "Instandhaltungsrücklage",
        en: "Maintenance Reserve",
      },
      W160: {
        de: "Hausgeld nicht umlagefähig",
        en: "Non-allocable Costs",
      },
      W180: {
        de: "Verwaltergebühr",
        en: "Management Fee",
      },
    };

    // Look up translation by condtype
    if (translations[condtype]) {
      return translations[condtype][currentLocale] || translations[condtype].en || translations[condtype].de;
    }

    // If condtype doesn't match any key, return the condtype value itself
    return condtype;
  };

  render() {
    const { initialLoading, errorMsg, selectedMonth } = this.state;
    const { condoIncome, condoIncomeError, condoIncomeLoading } =
      this.props.contractStore;

    // Get current locale for DatePicker
    const currentLocale = t.locale || "de";
    const datePickerLocale = currentLocale === "en" ? enUS : deDE;

    // Show full-page loader only on initial load
    if (initialLoading) {
      return <LoadingPageOverlay />;
    }

    if (errorMsg || condoIncomeError) {
      return (
        <div className="contract-list">
          <Alert
            role="alert"
            aria-live="assertive"
            message={errorMsg || condoIncomeError}
            type="error"
            style={{ marginBottom: "12px", marginTop: "12px" }}
          />
          <Button
            onClick={() => this.props.navigate(-1)}
            style={{ marginTop: "20px" }}
          >
            {t.BACK || "Back"}
          </Button>
        </div>
      );
    }

    const columns = [
      {
        title: t.CONDO_INCOME_TYPE,
        dataIndex: "condtype",
        key: "condtype",
        render: (text, record) => (
          <div style={{ fontWeight: "500" }}>
            {this.getIncomeTypeTranslation(record.condtype)}
          </div>
        ),
      },
      {
        title: t.CONDO_INCOME_NET,
        dataIndex: "monthvaluenet",
        key: "monthvaluenet",
        align: "right",
        render: (value) => this.formatCurrency(value),
      },
      {
        title: t.CONDO_INCOME_GROSS,
        dataIndex: "monthvaluegross",
        key: "monthvaluegross",
        align: "right",
        render: (value) => (
          <div style={{ fontWeight: "500" }}>{this.formatCurrency(value)}</div>
        ),
      },
    ];

    const dataSource = (condoIncome.items || []).filter((item) =>
      this.ALLOWED_CONDTYPES.includes(item.condtype)
    );
    const summary = condoIncome.summary || {
      totalNet: 0,
      totalGross: 0,
    };

    return (
      <div className="contract-list">
        <div style={{ marginBottom: "20px" }}>
          <h2>{t.CONDO_INCOME_TITLE}</h2>
          <p style={{ color: "#666" }}>
            {t.CONTRACT_NUMBER || "Contract Number"}:{" "}
            {this.props.useParams.contractNumber}
          </p>
        </div>

        <div style={{ marginBottom: "20px" }}>
          <label
            style={{
              display: "block",
              marginBottom: "8px",
              fontWeight: "500",
            }}
          >
            {t.CONDO_INCOME_SELECT_MONTH}:
          </label>
          <DatePicker
            picker="month"
            value={selectedMonth ? dayjs(selectedMonth, "YYYY-MM") : null}
            onChange={this.handleMonthChange}
            format="MMMM YYYY"
            locale={datePickerLocale}
            style={{ width: "200px" }}
            placeholder={t.CONDO_INCOME_SELECT_MONTH}
            allowClear
          />
        </div>

        {!selectedMonth ? (
          <div style={{ textAlign: "center", padding: "40px 20px", color: "#666" }}>
            <p style={{ fontSize: "16px" }}>
              {t.CONDO_INCOME_SELECT_MONTH}
            </p>
          </div>
        ) : dataSource.length === 0 && !condoIncomeLoading ? (
          <NoContent message={t.CONDO_INCOME_NO_DATA} image={ContractsImage} />
        ) : (
          <>
            <Table
              columns={columns}
              dataSource={dataSource}
              pagination={false}
              rowKey={(record, index) => record.condtype || index}
              loading={condoIncomeLoading}
              style={{ marginBottom: "20px" }}
              summary={() => (
                <Table.Summary fixed>
                  <Table.Summary.Row
                    style={{
                      backgroundColor: "#fafafa",
                      fontWeight: "bold",
                    }}
                  >
                    <Table.Summary.Cell index={0}>
                      <strong>{t.CONDO_INCOME_TOTAL}</strong>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={1} align="right">
                      <strong>{this.formatCurrency(summary.totalNet)}</strong>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={2} align="right">
                      <strong>{this.formatCurrency(summary.totalGross)}</strong>
                    </Table.Summary.Cell>
                  </Table.Summary.Row>
                </Table.Summary>
              )}
            />
          </>
        )}
      </div>
    );
  }
}

export default inject("contractStore")(withRouter(observer(CondoIncome)));

